import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIndexReader } from "./AppIndex";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppSearch, AppSearchChat, AppSearchData, AppSearchFilter, AppSearchHit, AppSearchMessage } from "./AppSearch";
import { AppSearchActView } from "./AppSearchActions";
import { AppSearchActRun } from "./AppSearchActRun";
import { AppSource, AppTextDone } from "./AppSource";

/** Hosted FTS search keeps contact labels from the archive, and never trusts response markup. */
export class AppSearchRemote {
    private Source: AppSource;
    private NextCursor: string = "";
    private Cursor: string = "";
    private Generation: number = 0;
    /** ⭐ (2026-10-22) Hits grouped with their actions when the panel has them (the service's answer carries the id). */
    private Acts: AppSearchActRun | null = null;
    public constructor(Source: AppSource, Acts: AppSearchActRun | null = null) { this.Source = Source; this.Acts = Acts; }
    public Reset = (): void => { this.Cursor = ""; this.NextCursor = ""; this.Generation++; };
    public More = (): void => { this.Cursor = this.NextCursor; };
    public Stop = (): void => { this.Generation++; };

    public Search = (Data: AppSearchData, Query: string, Filter: AppSearchFilter, Done: AppTextDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Ask: Record<string, unknown> = { text: Query, limit: AppSearch.Max, cursor: this.Cursor };
            if (Filter.Href !== "") {
                for (const Chat of Data.Chats) { if (Chat.Href === Filter.Href) { Ask.chatId = AppSearchRemote.ChatId(Chat.ChatId, Chat.PersonKey); } }
            }
            if (Filter.FromMe !== "") { Ask.fromMe = Filter.FromMe === "me"; }
            if (Filter.After !== "") { Ask.after = Filter.After + "T00:00:00.000Z"; }
            if (Filter.Before !== "") { Ask.before = Filter.Before + "T23:59:59.999Z"; }
            const Generation: number = ++this.Generation;
            const Self: AppSearchRemote = this;
            function Answer(Text: string): void {
                if (Generation === Self.Generation) { Done(Self.Html(Data, Query, Text)); }
            }
            this.Source.QueryMessages(Ask, Answer);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.Search"); }
    };

    private Html = (Data: AppSearchData, Query: string, Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Raw = AppIndexReader.ObjectIn(Text);
            if (Raw === null || Raw.ok !== true || Array.isArray(Raw.messages) === false) {
                return AppSearch.Head("Messages could not be searched just now. Try again in a moment.");
            }
            if (Raw.stale === true) { this.Reset(); return AppSearch.Head("Your archive changed. Search again to refresh the results."); }
            const Words: string[] = AppSearch.Words(Query);
            if (this.Acts !== null) { return this.Grouped(Data, Words, Raw); }
            const Out: string[] = [AppSearch.Head(AppSearchRemote.CountLine(Raw.total)), '<ul class="hits">'];
            for (const Msg of Raw.messages as Record<string, unknown>[]) {
                if (Msg === null || typeof Msg !== "object" || typeof Msg.text !== "string" || typeof Msg.messageId !== "string") { continue; }
                let Title: string = String(Msg.chatId);
                for (const Chat of Data.Chats) { if (AppSearchRemote.ChatId(Chat.ChatId, Chat.PersonKey) === Msg.chatId) { Title = Chat.Title; break; } }
                let Shown: string = Msg.text;
                if (typeof Msg.translation === "string" && AppSearch.Matches(AppSearch.Fold(Shown), Words) === false
                    && AppSearch.Matches(AppSearch.Fold(Msg.translation), Words) === true) { Shown = Msg.translation; }
                    Out.push('<li class="hit"><button type="button" data-search-chat="' + kHTMLCore.EscapeAttr(String(Msg.chatId))
                        + '" data-search-message="' + kHTMLCore.EscapeAttr(Msg.messageId) + '"><span class="who">'
                        + kHTMLCore.EscapeHtml(Title) + '</span><span class="when">' + kHTMLCore.EscapeHtml(AppSearchRemote.WhenLine(Msg))
                        + '</span><span class="snip">' + AppSearch.Snippet(Shown, Words, AppSearch.Radius) + '</span></button></li>');
            }
            Out.push('</ul>');
            Out.push(this.PagesHtml(Raw));
            return Out.join("");
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.Html"); return ""; }
    };

    /** The paging buttons under the service's hits (and the cursor they continue from). */
    private PagesHtml = (Raw: Record<string, unknown>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            this.NextCursor = "";
            if (typeof Raw.nextCursor === "string") { this.NextCursor = Raw.nextCursor; }
            const Pages: string[] = [];
            if (this.NextCursor !== "") { Pages.push(AppSearch.Pager("data-search-more", "1", "More messages")); }
            if (this.Cursor !== "") { Pages.push(AppSearch.Pager("data-search-first", "1", "First messages")); }
            if (Pages.length === 0) { return ""; }
            return '<p class="hitsmore">' + Pages.join("") + '</p>';
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.PagesHtml"); return ""; }
    };

    /** ⭐ The service's hits as `AppSearchHit`s, grouped per contact with their actions (`AppSearchActView`). */
    private Grouped = (Data: AppSearchData, Words: string[], Raw: Record<string, unknown>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Run: AppSearchActRun = this.Acts as AppSearchActRun;
            const Hits: AppSearchHit[] = [];
            for (const Msg of Raw.messages as Record<string, unknown>[]) {
                const Hit: AppSearchHit | null = AppSearchRemote.HitOf(Data, Words, Msg);
                if (Hit !== null) { Hit.Order = Hits.length; Hits.push(Hit); }
            }
            Run.Opts.Remote = true;
            return AppSearch.Head(AppSearchRemote.CountLine(Raw.total)) + AppSearchActView.Groups(Hits, Words, Run.Opts, Run.Hits)
                + this.PagesHtml(Raw);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.Grouped"); return ""; }
    };

    /** One service hit, or null when it lacks words or an id. The chat is the archive's own when known (title, href). */
    public static HitOf = (Data: AppSearchData, Words: string[], Msg: Record<string, unknown>): AppSearchHit | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Msg === null || typeof Msg !== "object" || typeof Msg.text !== "string" || typeof Msg.messageId !== "string") { return null; }
            const Hit: AppSearchHit = new AppSearchHit();
            let Chat: AppSearchChat | null = null;
            for (const One of Data.Chats) { if (AppSearchRemote.ChatId(One.ChatId, One.PersonKey) === Msg.chatId) { Chat = One; break; } }
            if (Chat === null) { Chat = new AppSearchChat(); Chat.ChatId = String(Msg.chatId); Chat.Title = String(Msg.chatId); }
            Hit.Chat = Chat;
            const One: AppSearchMessage = new AppSearchMessage();
            One.MessageId = Msg.messageId; One.Text = Msg.text; One.FromMe = Msg.fromMe === true;
            if (typeof Msg.at === "string") { One.Minute = (Msg.at as string).substring(0, 16); }
            if (typeof Msg.translation === "string" && AppSearch.Matches(AppSearch.Fold(One.Text), Words) === false
                && AppSearch.Matches(AppSearch.Fold(Msg.translation), Words) === true) {
                One.Translation = Msg.translation; Hit.ViaTranslation = true;
            }
            Hit.Msg = One;
            return Hit;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.HitOf"); return null; }
    };

    /**
     * The line under "Messages" for the service's answer, in the local search's words (`AppSearch.CountLine`): it
     * said "1 matching messages" and "0 matching messages" (audit 2026-10-12). A total that is not a number says nothing
     * about a count.
     */
    public static CountLine = (Total: unknown): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Total !== "number" || isFinite(Total) === false || Total < 0) { return "Matching messages"; }
            if (Total === 0) { return "No messages match."; }
            if (Total === 1) { return "1 matching message"; }
            return Math.round(Total).toLocaleString() + " matching messages";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.CountLine");
            return "";
        }
    };

    /**
     * A hit's second line: when it was said, on the reader's own clock, and "You" for the owner's own words - the
     * local search's line (`AppSearch.HitHtml`). ⭐ It was the wire's "2026-10-12T07:05:00.000Z" (audit 2026-10-12).
     */
    private static WhenLine = (Msg: Record<string, unknown>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Bits: string[] = [];
            const When: string = AppSearch.WhenOf(Msg.at);
            if (When.length > 0) { Bits.push(When); }
            if (Msg.fromMe === true) { Bits.push("You"); }
            return Bits.join("  ·  ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemote.WhenLine");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: pure compatibility fallback for archives built before chatId was published. */
    private static ChatId = (Id: string, Person: string): string => {
        if (Id !== "") { return Id; } return Person.replace(/^\+/, "") + "@s.whatsapp.net";
    };
}
